import React, { useState } from "react";
import "./AdminDashboard.css";

// Template image
import templateImage from "../assets/images/wordsearchtemplate1.png";

// TinyMCE Editor + Plugins
import { Editor } from "@tinymce/tinymce-react";
import "tinymce/tinymce.min.js";
import "tinymce/themes/silver/theme.min.js";
import "tinymce/icons/default/icons.min.js";

import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/table";
import "tinymce/plugins/image";
import "tinymce/plugins/autoresize";
import "tinymce/plugins/anchor";
import "tinymce/plugins/autolink";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/codesample";

// html2canvas
import html2canvas from "html2canvas";

export default function WordsearchGenerator() {
  const [word, setWord] = useState("");
  const [textHTML, setTextHTML] = useState("");
  const [previewURL, setPreviewURL] = useState("");

  const TEMPLATE_SRC = templateImage;

  // ------------------------------------------------
  // 1. Generate Wordsearch Grid
  // ------------------------------------------------
  function generateWordGrid(word, gridSize = 12) {
    word = word.toUpperCase();

    const grid = Array.from({ length: gridSize }, () =>
      Array.from({ length: gridSize }, () => "")
    );

    const startRow = Math.floor(Math.random() * gridSize);
    const startCol = Math.floor(Math.random() * (gridSize - word.length));

    for (let i = 0; i < word.length; i++) {
      grid[startRow][startCol + i] = word[i];
    }

    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    for (let r = 0; r < gridSize; r++) {
      for (let c = 0; c < gridSize; c++) {
        if (!grid[r][c]) {
          grid[r][c] = letters[Math.floor(Math.random() * letters.length)];
        }
      }
    }

    return grid;
  }

  // ------------------------------------------------
  // 2. Generate final image (template + grid + HTML content)
  // ------------------------------------------------
  async function generateFinalImage() {
    if (!word.trim()) return alert("Enter a word first!");

    const gridSize = 12;
    const grid = generateWordGrid(word, gridSize);

    const template = new Image();
    template.src = TEMPLATE_SRC;
    template.crossOrigin = "anonymous";

    return new Promise((resolve) => {
      template.onload = async () => {
        const canvas = document.createElement("canvas");
        canvas.width = template.width;
        canvas.height = template.height;

        const ctx = canvas.getContext("2d");

        // Draw template
        ctx.drawImage(template, 0, 0);

        // ------------------------------------------------
        // Wordsearch box area
        // ------------------------------------------------
        const boxX = 75;
        const boxY = 299;
        const boxW = 1036;
        const boxH = 1041;

        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(boxX, boxY, boxW, boxH);

        const cellW = boxW / gridSize;
        const cellH = boxH / gridSize;

        ctx.fillStyle = "#000";
        ctx.font = `${Math.min(cellW, cellH) * 0.6}px Arial`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        // Draw grid letters
        for (let r = 0; r < gridSize; r++) {
          for (let c = 0; c < gridSize; c++) {
            const x = boxX + c * cellW + cellW / 2;
            const y = boxY + r * cellH + cellH / 2;
            ctx.fillText(grid[r][c], x, y);
          }
        }

        // ------------------------------------------------
        // Rich HTML rendering section (right-hand side)
        // ------------------------------------------------
        const textX = 1151;
        const textY = 300;
        const textWidth = 800;

        const wysiwygElement = document.getElementById("wysiwyg-render");

        // Render TinyMCE HTML + images
        const wysiwygCanvas = await html2canvas(wysiwygElement, {
          backgroundColor: null,
          scale: 2,
        });

        ctx.drawImage(
          wysiwygCanvas,
          textX,
          textY,
          textWidth,
          wysiwygCanvas.height
        );

        resolve(canvas.toDataURL("image/png"));
      };
    });
  }

  // ------------------------------------------------
  // 3. When clicking "Generate"
  // ------------------------------------------------
  async function handleGenerate() {
    const result = await generateFinalImage();
    setPreviewURL(result);
  }

  // ------------------------------------------------
  // 4. Render UI
  // ------------------------------------------------
  return (
    <div className="admin-page">
      <h1>Wordsearch Generator</h1>
      <p className="admin-subtitle">
        Generate wordsearch images with full rich text & drag-drop images.
      </p>

      <div className="admin-form-large" style={{ maxWidth: "900px" }}>

        {/* WORD INPUT */}
        <label className="admin-label">Word to Hide</label>
        <input
          className="admin-input"
          value={word}
          onChange={(e) => setWord(e.target.value)}
          placeholder="Enter hidden word"
        />

        {/* RICH TEXT EDITOR */}
        <label className="admin-label">Right-Side Text (Rich Format + Images)</label>

        <Editor
          value={textHTML}
          init={{
            license_key: "gpl",
            base_url: "/tinymce",
            suffix: ".min",
            height: 300,
            menubar: false,
            plugins: [
              "lists",
              "link",
              "image",
              "table",
              "autoresize",
              "anchor",
              "searchreplace",
              "autolink",
              "codesample",
            ],
            toolbar:
              "undo redo | bold italic underline | " +
              "bullist numlist | link image | alignleft aligncenter alignright | forecolor",

            skin: "oxide",
            content_css: "default",

            content_style: `
              body { 
                background-color: #ffffff;
                padding: 12px; 
                font-size: 20px;
                line-height: 32px;
              }
            `,

            // ⭐ ENABLE DRAG-DROP IMAGES (embedded base64)
            paste_data_images: true,
            automatic_uploads: false,
            images_upload_handler: function (blobInfo, success) {
              success(
                "data:" +
                  blobInfo.blob().type +
                  ";base64," +
                  blobInfo.base64()
              );
            },
          }}
          onEditorChange={(content) => setTextHTML(content)}
        />

        {/* Hidden WYSIWYG container (for html2canvas) */}
        <div
          id="wysiwyg-render"
          style={{
            position: "absolute",
            opacity: 0,
            pointerEvents: "none",
            zIndex: -1,
            width: "800px",
            fontSize: "48px",
            lineHeight: "60px",
          }}
          dangerouslySetInnerHTML={{ __html: textHTML }}
        ></div>

        {/* GENERATE BUTTON */}
        <button
          className="admin-button"
          style={{ marginTop: "20px" }}
          onClick={handleGenerate}
        >
          Generate Wordsearch Image
        </button>

        {/* PREVIEW */}
        {previewURL && (
          <>
            <h3 style={{ marginTop: "20px" }}>Preview</h3>
            <img
              src={previewURL}
              alt="Preview"
              style={{
                width: "100%",
                borderRadius: "8px",
                border: "1px solid #ccc",
              }}
            />

            <a
              className="admin-button secondary"
              href={previewURL}
              download="wordsearch.png"
              style={{ marginTop: "12px" }}
            >
              Download PNG
            </a>
          </>
        )}
      </div>
    </div>
  );
}

